import { Autorenew } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react'
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from "@mui/icons-material/People";
import FilterListIcon from "@mui/icons-material/FilterList";
import HistoryIcon from "@mui/icons-material/History";
import { UrlApi } from '../../../services/apirest';
import Navbar from '../../Navbar';
import ActualizarPreciosMasivo from './ActualizarPreciosMasivo';
import GrupoPrecios from './GrupoPrecios';
import FiltrosPrecios, { CRITERIOS, valorCriterio } from './FiltrosPrecios';
import HistorialPreciosEspeciales from './HistorialPreciosEspeciales';

// Normaliza texto para buscar sin importar mayúsculas ni acentos
const normalizar = (texto) =>
  (texto ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const compararTexto = (a, b) => (a ?? "").localeCompare(b ?? "");

// Opciones de "Agrupar por": cliente, producto y el resto de los criterios de filtro
const AGRUPACIONES = [
  { valor: "cliente", label: "Cliente" },
  { valor: "producto", label: "Producto" },
  ...CRITERIOS.filter((c) => c.campo !== "producto").map((c) => ({
    valor: c.campo,
    label: c.label,
  })),
];

function ListaPreciosEspeciales() {
  const [precios, setPrecios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [filtros, setFiltros] = useState({});
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [verHistorial, setVerHistorial] = useState(false);
  const [agruparPor, setAgruparPor] = useState("cliente");
  const [seleccion, setSeleccion] = useState({});
  const [expandidos, setExpandidos] = useState({});
  const [openActualizar, setOpenActualizar] = useState(false);
  const [openSnack, setOpenSnack] = useState(false);

  const obtenerPrecios = async () => {
    const sucAuth = localStorage.getItem("suc_auth")?.split(",") || [];
    if (sucAuth.length === 0) {
      setPrecios([]);
      setCargando(false);
      return;
    }
    setCargando(true);
    setErrorCarga(false);
    try {
      const res = await fetch(`${UrlApi}/envasado_clientes/listapreciosespecial/${sucAuth}`);
      if (!res.ok) throw new Error("Error al obtener precios especiales");
      const data = await res.json();
      setPrecios(data ?? []);
    } catch (err) {
      console.error(err);
      setErrorCarga(true);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    obtenerPrecios();
  }, []);

  // Sucursales presentes en los datos (ya vienen filtradas por suc_auth)
  const sucursales = useMemo(() => {
    const mapa = new Map();
    precios.forEach((p) => mapa.set(p.id_sucursal, p.nombre_sucursal));
    return [...mapa.entries()].map(([id, nombre]) => ({ id, nombre }));
  }, [precios]);

  // Opciones de cada filtro con la cantidad de precios, armadas desde los datos
  const opciones = useMemo(() => {
    const resultado = {};
    CRITERIOS.forEach((c) => {
      const mapa = new Map();
      precios.forEach((p) => {
        const { id, nombre } = valorCriterio(c, p);
        if (!mapa.has(id)) mapa.set(id, { id, nombre, cant: 0 });
        mapa.get(id).cant++;
      });
      resultado[c.campo] = [...mapa.values()].sort((a, b) => compararTexto(a.nombre, b.nombre));
    });
    return resultado;
  }, [precios]);

  const cantFiltrosActivos = Object.values(filtros).filter((v) => v.length > 0).length;

  const handleChangeFiltro = (campo, valor) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
  };

  const filtrados = useMemo(() => {
    const texto = normalizar(busqueda.trim());
    // Entre criterios aplica AND; dentro de un mismo criterio, OR
    const activos = CRITERIOS.filter((c) => (filtros[c.campo] ?? []).length > 0).map((c) => ({
      criterio: c,
      ids: new Set(filtros[c.campo].map((o) => o.id)),
    }));
    return precios.filter(
      (p) =>
        activos.every(({ criterio, ids }) => ids.has(valorCriterio(criterio, p).id)) &&
        (texto === "" ||
          normalizar(p.nombre_cliente).includes(texto) ||
          normalizar(p.nombre_producto).includes(texto))
    );
  }, [precios, busqueda, filtros]);

  const grupos = useMemo(() => {
    const criterio = CRITERIOS.find((c) => c.campo === agruparPor);
    const mapa = new Map();
    filtrados.forEach((p) => {
      let id, titulo;
      if (agruparPor === "cliente") {
        id = p.cliente;
        titulo = p.nombre_cliente;
      } else {
        ({ id, nombre: titulo } = valorCriterio(criterio, p));
      }
      const key = `${agruparPor}_${id}`;
      if (!mapa.has(key)) mapa.set(key, { key, titulo, subtitulo: null, items: [] });
      mapa.get(key).items.push(p);
    });
    const lista = [...mapa.values()];
    lista.forEach((g) => {
      if (agruparPor === "cliente") {
        g.subtitulo = g.items[0].nombre_sucursal;
      } else if (agruparPor === "producto") {
        g.items.sort((a, b) => compararTexto(a.nombre_cliente, b.nombre_cliente));
        g.subtitulo = `${g.items.length} clientes`;
      } else {
        g.items.sort(
          (a, b) =>
            compararTexto(a.nombre_cliente, b.nombre_cliente) ||
            compararTexto(a.nombre_producto, b.nombre_producto)
        );
        const cantCli = new Set(g.items.map((p) => p.cliente)).size;
        g.subtitulo = `${cantCli} clientes · ${g.items.length} precios`;
      }
    });
    return lista.sort((a, b) => compararTexto(a.titulo, b.titulo));
  }, [filtrados, agruparPor]);

  const seleccionados = useMemo(
    () => precios.filter((p) => seleccion[p.id]),
    [precios, seleccion]
  );

  const cantClientes = useMemo(
    () => new Set(filtrados.map((p) => p.cliente)).size,
    [filtrados]
  );

  const todosFiltradosSeleccionados =
    filtrados.length > 0 && filtrados.every((p) => seleccion[p.id]);
  const algunosFiltradosSeleccionados =
    !todosFiltradosSeleccionados && filtrados.some((p) => seleccion[p.id]);

  const marcarItems = (items, marcar) => {
    setSeleccion((prev) => {
      const nueva = { ...prev };
      items.forEach((item) => {
        if (marcar) nueva[item.id] = true;
        else delete nueva[item.id];
      });
      return nueva;
    });
  };

  const toggleItem = (id) => {
    setSeleccion((prev) => {
      const nueva = { ...prev };
      if (nueva[id]) delete nueva[id];
      else nueva[id] = true;
      return nueva;
    });
  };

  const toggleExpandido = (key) => {
    setExpandidos((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleGuardado = () => {
    setOpenActualizar(false);
    setSeleccion({});
    setOpenSnack(true);
    obtenerPrecios();
  };

  const buscando = busqueda.trim() !== "";

  if (verHistorial) {
    return (
      <div>
        <Navbar />
        <Container maxWidth="lg" sx={{ py: 2 }}>
          <HistorialPreciosEspeciales onClose={() => setVerHistorial(false)} />
        </Container>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <Container maxWidth="lg" sx={{ py: 2, pb: seleccionados.length > 0 ? 14 : 6 }}>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
            <Typography variant="poster" fontSize={26} mr={2}>
              <PeopleIcon sx={{ fontSize: 20 }} /> Precios especiales
            </Typography>
            <Chip label={`${cantClientes} clientes`} />
            <Chip label={`${filtrados.length} precios`} />
            {seleccionados.length > 0 && (
              <Chip color="primary" label={`${seleccionados.length} seleccionados`} />
            )}
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setVerHistorial(true)}
              sx={{ ml: "auto" }}
            >
              Historial
            </Button>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Buscar cliente o producto..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              label="Agrupar por"
              value={agruparPor}
              onChange={(e) => {
                setAgruparPor(e.target.value);
                setExpandidos({});
              }}
              sx={{ minWidth: 200 }}
            >
              {AGRUPACIONES.map((a) => (
                <MenuItem key={a.valor} value={a.valor}>
                  {a.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant={cantFiltrosActivos > 0 ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setMostrarFiltros((prev) => !prev)}
            >
              Filtros{cantFiltrosActivos > 0 ? ` (${cantFiltrosActivos})` : ""}
            </Button>
            {cantFiltrosActivos > 0 && (
              <Button size="small" color="inherit" onClick={() => setFiltros({})}>
                Limpiar filtros
              </Button>
            )}
          </Box>

          <Collapse in={mostrarFiltros}>
            <Box mt={2}>
              <FiltrosPrecios
                opciones={opciones}
                filtros={filtros}
                onChangeFiltro={handleChangeFiltro}
              />
            </Box>
          </Collapse>
        </Paper>

        {cargando ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : errorCarga ? (
          <Alert severity="error">No se pudieron cargar los precios especiales.</Alert>
        ) : grupos.length === 0 ? (
          <Alert severity="info">
            {precios.length === 0
              ? "No hay precios especiales para tus sucursales."
              : "No hay resultados para los filtros aplicados."}
          </Alert>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} px={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={todosFiltradosSeleccionados}
                    indeterminate={algunosFiltradosSeleccionados}
                    onChange={() => marcarItems(filtrados, !todosFiltradosSeleccionados)}
                  />
                }
                label={`Seleccionar todo lo filtrado (${filtrados.length})`}
              />
              <Box>
                <Button
                  size="small"
                  onClick={() => {
                    const todos = {};
                    grupos.forEach((g) => (todos[g.key] = true));
                    setExpandidos(todos);
                  }}
                >
                  Expandir todo
                </Button>
                <Button size="small" onClick={() => setExpandidos({})}>
                  Contraer todo
                </Button>
              </Box>
            </Box>

            {grupos.map((g) => (
              <GrupoPrecios
                key={g.key}
                titulo={g.titulo}
                subtitulo={g.subtitulo}
                items={g.items}
                etiquetaItem={(item) =>
                  agruparPor === "cliente" ? item.nombre_producto : item.nombre_cliente
                }
                detalleItem={
                  agruparPor === "cliente"
                    ? null
                    : agruparPor === "producto"
                    ? sucursales.length > 1
                      ? (item) => item.nombre_sucursal
                      : null
                    : (item) => item.nombre_producto
                }
                seleccion={seleccion}
                onToggleItem={toggleItem}
                onToggleGrupo={marcarItems}
                expandido={buscando || !!expandidos[g.key]}
                onToggleExpandido={() => toggleExpandido(g.key)}
              />
            ))}
          </>
        )}
      </Container>

      {seleccionados.length > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            borderTop: "3px solid #2471A3",
          }}
        >
          <Container
            maxWidth="lg"
            sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5 }}
          >
            <Typography fontWeight="bold" flexGrow={1}>
              {seleccionados.length} precios seleccionados
            </Typography>
            <Button color="inherit" onClick={() => setSeleccion({})}>
              Limpiar
            </Button>
            <Button
              variant="contained"
              startIcon={<Autorenew />}
              onClick={() => setOpenActualizar(true)}
            >
              Actualizar precios
            </Button>
          </Container>
        </Paper>
      )}

      {openActualizar && (
        <ActualizarPreciosMasivo
          open={openActualizar}
          onClose={() => setOpenActualizar(false)}
          seleccionados={seleccionados}
          onGuardado={handleGuardado}
        />
      )}
      <Snackbar
        open={openSnack}
        autoHideDuration={2000}
        onClose={() => setOpenSnack(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setOpenSnack(false)}>
          Los precios se actualizaron correctamente
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ListaPreciosEspeciales
